import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/cartContext";
import { toast } from "react-toastify";

const CartPage = () => {
  const { cartItems, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  // Toggle features display for a cart item
  const toggleFeatures = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Mock coupon application (e.g., 10% off for "SAVE10")
  const applyCoupon = () => {
    if (coupon.toUpperCase() === "SAVE10") {
      setDiscount(0.1); // 10% discount
      toast.success("Coupon applied! 10% off your total.");
    } else {
      setDiscount(0);
      toast.error("Invalid coupon code.");
    }
  };

  // Calculate subtotal and total with discount
  const subtotal = cartItems.reduce((total, item) => total + item.price, 0);
  const total = subtotal * (1 - discount);

  // Plan tier badge styles
  const getPlanBadgeClass = (planName) => {
    const plan = planName.toLowerCase().split(" ")[0];
    switch (plan) {
      case "basic":
        return "badge bg-primary";
      case "lite":
        return "badge bg-success";
      case "premium":
        return "badge bg-warning text-dark";
      case "enterprise":
        return "badge bg-purple";
      default:
        return "badge bg-secondary";
    }
  };

  // Get 2-3 key features for summary
  const getKeyFeatures = (features) => {
    return features.slice(0, 3); // Show first 3 features as highlights
  };

  return (
    <div className="bg-light min-vh-100">
      <div className="container py-5">
        {cartItems.length === 0 ? (
          <div className="text-center py-5">
            <h4 className="mb-3">🛒 Your cart is empty</h4>
            <p className="text-muted mb-4">
              Purchase some services from our <strong>Pricing & Plans</strong> page to get started.
            </p>
            <Link to="/pricing" className="btn btn-primary px-4">
              Go to Pricing & Plans
            </Link>
          </div>
        ) : (
          <div className="row">
            <div className="col-md-8">
              <h3 className="fw-bold mb-4">PRODUCTS</h3>
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="mb-4 p-3 bg-white shadow-sm rounded border"
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="fw-semibold text-success mb-1">{item.name}</h4>
                      {/* <span className={getPlanBadgeClass(item.name)}>{item.name}</span> */}
                      <span className="badge bg-light text-dark small ms-2">{item.category}</span>
                      <div className="mt-2">
                        <h6 className="fw-bold">Key Benefits:</h6>
                        <ul className="list-unstyled">
                          {getKeyFeatures(item.features || []).map((feature, index) => (
                            <li key={index} className="text-muted small">
                              <i className="fas fa-check me-1"></i> {feature}
                            </li>
                          ))}
                        </ul>
                        {item.features && item.features.length > 3 && (
                          <span
                            className="text-primary text-decoration-underline cursor-pointer"
                            onClick={() => toggleFeatures(item.id)}
                          >
                            {expandedItems[item.id] ? "Show Less" : "Show More"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex flex-row align-items-end">
                      <h5 className="fw-bold text-dark m-0 me-3">
                        ₹{item.price.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        <small>/pm</small>
                      </h5>
                     
                      <button
                        onClick={() => removeFromCart(item.id)}
                        title="Remove item"
                        style={{
                          color: "#dc3545",
                          fontSize: "1.2rem",
                          border: "none",
                          background: "transparent",
                        }}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                  {expandedItems[item.id] && item

.features && (
                    <div className="mt-3">
                      <h6 className="fw-bold">All Features:</h6>
                      <ul className="list-unstyled">
                        {item.features.map((feature, index) => (
                          <li key={index} className="text-muted small">
                            <i className="fas fa-check me-1"></i> {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="col-md-4">
              <div className="bg-white border p-4 rounded shadow-sm cart-section">
                <h4 className="fw-bold mb-3">CART TOTALS</h4>

                <div className="mb-3">
                  <div className="accordion mb-3" id="couponAccordion">
                    <div className="accordion-item border-0">
                      <h2 className="accordion-header" id="headingCoupon">
                        <button
                          className="accordion-button collapsed px-0"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseCoupon"
                          aria-expanded="false"
                          aria-controls="collapseCoupon"
                        >
                          Have a coupon?
                        </button>
                      </h2>
                      <div
                        id="collapseCoupon"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingCoupon"
                        data-bs-parent="#couponAccordion"
                      >
                        <div className="accordion-body px-0">
                          <div className="d-flex">
                            <input
                              type="text"
                              className="form-control me-2"
                              id="coupon"
                              placeholder="Enter coupon code"
                              value={coupon}
                              onChange={(e) => setCoupon(e.target.value)}
                            />
                            <button className="btn btn-outline-primary" onClick={applyCoupon}>
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                </div>

                {discount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-success">
                    <span>Discount (10%)</span>
                    <span>-₹{(subtotal * discount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                <div className="d-flex justify-content-between mb-2">
                  <span>Delivery</span>
                  <span className="fw-bold">FREE</span>
                </div>

                <div className="d-flex justify-content-between border-top pt-3 mt-3 fw-bold">
                  <span>Total</span>
                  <span>₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                </div>

                <button
                  className="btn btn-primary w-100 mt-4 py-2 fw-bold"
                  onClick={() => navigate("/checkout")}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .bg-purple {
          background-color: #6f42c1;
          color: white;
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default CartPage;